import { IController, IPromise, IQService, IScope, equals } from "angular";
import { HierarchyNode } from "d3-hierarchy";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { generateDefaultMandatoryOkrAlignmentAccountSettings } from "@gtmhub/configuration/utils/utils";
import { IRestLayerRequest } from "@gtmhub/core";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import dayjs from "@gtmhub/dayjs";
import { IIndicator, IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { FavoritesManager } from "@gtmhub/favorites/favorites.manager";
import { FavoritesManagerFactory } from "@gtmhub/favorites/favorites.manager.factory";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { CurrentUserService, getCurrentUserId } from "@gtmhub/users";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { IMandatoryOkrAlignmentSettings } from "@webapp/configuration/models/configuration.model";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";
import { IStratifiedPlanningSession, Session } from "@webapp/sessions/models/sessions.model";
import { TrackingEvents } from "@webapp/shared/utils/tracking";
import { PlanningSessionsActions } from "../redux/session-actions";
import { ISessionsStoreState } from "../redux/session-reducer";
import { filterActiveSessionsWithoutCheckDate } from "../redux/session-selectors";
import { Ng1ConfirmDeleteSessionService } from "../services/ng1-confirm-delete-session.service";
import { calculateSessionsTimeframe, createSessionTree, generateTimePeriods } from "./utils";

const bindStateToScope = (state: ISessionsStoreState): IPlanningSessionsRedux => ({
  allSessions: state.sessions.items,
  allActiveSessions: filterActiveSessionsWithoutCheckDate(state.sessions.items),
  sessionsFetched: state.sessions.isFetched,
  fetchSessionsError: state.sessions.error,
});

interface IPlanningSessionsRedux {
  allSessions: Session[];
  allActiveSessions: Session[];
  sessionsFetched: boolean;
  fetchSessionsError: IUIError;
}

interface IAllowedActionsForSession {
  [sessionId: string]: string[];
}

export interface IPlanningSessionsScope extends IScope, IExtendedRootScopeService, IPlanningSessionsRedux {
  timeframe: number;
  timePeriods: Record<string, string[]>;
  todaysDate: {
    date: string;
    leftPercentage: number;
  };
  allowedActionsPerSession: IAllowedActionsForSession;
  showAllSessions: boolean;
  isOkrAlignmentMandatory: boolean;
  sessions: HierarchyNode<Session>[];
  indicators: {
    loading?: IIndicator;
  };
  hasGoalsWithoutSession: boolean;

  confirmDeletePlanningSession(planningSession: Session): void;
  onToggleSessions(): void;
  favoritesManager: FavoritesManager;
  getNavItem(list: Session): INavItem;
  sessionIsFavorite(list: Session): boolean;
}

export class PlanningSessionsCtrl implements IController {
  private firstLoadOfCtrl = true;
  private favoritesManager: FavoritesManager;

  public static $inject = [
    "$rootScope",
    "$scope",
    "UIErrorHandlingService",
    "$ngRedux",
    "PlanningSessionsActions",
    "Ng1ConfirmDeleteSessionService",
    "CurrentUserService",
    "AnalyticsService",
    "FeatureTogglesFacade",
    "FavoritesManagerFactory",
    "AccountService",
    "$q",
    "GoalService",
  ];

  constructor(
    private $rootScope: IGtmhubRootScopeService & ITraceRootScopeService,
    private $scope: IPlanningSessionsScope,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private confirmDeleteSessionService: Ng1ConfirmDeleteSessionService,
    private currentUserService: CurrentUserService,
    private analyticsService: AnalyticsService,
    private featureToggleService: FeatureTogglesFacade,
    private favoritesManagerFactory: FavoritesManagerFactory,
    private accountService: AccountService,
    private $q: IQService,
    private goalService: GoalService
  ) {
    this.$scope.indicators = { loading: { progress: true } };
    this.$rootScope.noNavigation = false;
    this.$scope.allowedActionsPerSession = {};
    this.$scope.showAllSessions = this.currentUserService.getUserSetting<boolean>("showArchivedSessions") || false;
    this.favoritesManager = this.favoritesManagerFactory(["session"]);

    this.$scope.confirmDeletePlanningSession = this.confirmDeletePlanningSession;
    this.$scope.onToggleSessions = this.onToggleSessions;

    this.$scope.favoritesManager = this.favoritesManager;
    this.$scope.getNavItem = this.getNavItem;
    this.$scope.sessionIsFavorite = this.sessionIsFavorite;
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());

    this.init();
  }

  private init = (): void => {
    this.watchForGoalsWithoutSession();
    this.fetchData()
      .then(() => {
        this.processSessions();
        delete this.$scope.indicators.loading;
        this.initSessionsChangedWatch();
      })
      .catch((error) => {
        this.$scope.indicators.loading = { error };
      });
  };

  private fetchData = (): IPromise<boolean | void> => {
    return this.$q
      .all({
        mandatoryOkrAlignmentToggleEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.MandatoryOkrAlignmentFeature),
        hasGoalsWithoutSession: this.checkForGoalsWithoutSession(),
        sessions: this.fetchSessionsPromise(),
      })
      .then(({ mandatoryOkrAlignmentToggleEnabled, hasGoalsWithoutSession }) => {
        const mandatoryOkrAlignmentSettings: IMandatoryOkrAlignmentSettings =
          this.accountService.getAccountSetting("mandatoryOkrAlignment") || generateDefaultMandatoryOkrAlignmentAccountSettings();
        this.$scope.isOkrAlignmentMandatory =
          mandatoryOkrAlignmentToggleEnabled && mandatoryOkrAlignmentSettings.enabled && !mandatoryOkrAlignmentSettings.excludedUserIds.includes(getCurrentUserId());
        this.favoritesManager.initFavorites();

        this.$scope.hasGoalsWithoutSession = hasGoalsWithoutSession;
      });
  };

  private processSessions = (): void => {
    const sessions: Session[] = !this.$scope.showAllSessions ? this.$scope.allActiveSessions : this.$scope.allSessions;

    this.$scope.sessions = createSessionTree(sessions)?.children || [];

    if (!sessions.length) {
      // we don't want to track "Sessions Page Data Loaded" when there are no sessions - https://gtmhub.atlassian.net/browse/GVS-7970
      this.firstLoadOfCtrl = false;
      // we don't need to render the screen if there are no sessions yet
      return;
    } else if (this.firstLoadOfCtrl) {
      this.trackSessionsPageDataLoaded(sessions);
      this.firstLoadOfCtrl = false;
    }

    sessions.forEach((session) => {
      this.$scope.allowedActionsPerSession[session.id] = this.currentUserService.allowedActionsSync(session.access);
    });

    const { startDate, endDate, timeframe } = calculateSessionsTimeframe(sessions);

    // calculate what part is a day of timeframe
    const dayPartInTimeframe = 100 / timeframe;

    for (let i = 0; i < sessions.length; i++) {
      const daysLength = dayjs(sessions[i].end).diff(sessions[i].start, "days");
      sessions[i].daysLength = daysLength;
      sessions[i].lengthPercentage = dayPartInTimeframe * daysLength;
      sessions[i].leftPercentage = dayPartInTimeframe * dayjs(sessions[i].start).diff(startDate, "days");
    }

    this.$scope.timePeriods = generateTimePeriods(startDate, endDate);
    this.$scope.timeframe = timeframe;
    this.$scope.todaysDate = {
      date: dayjs().toISOString(),
      leftPercentage: dayPartInTimeframe * dayjs().diff(startDate, "days"),
    };
  };

  private fetchSessionsPromise = (): IPromise<void> => {
    return this.$scope.watchUntilAndRejectOnError(
      () => this.$scope.sessionsFetched,
      () => this.$scope.fetchSessionsError
    );
  };

  private initSessionsChangedWatch = (): void => {
    this.$scope.$watch("allSessions", (newSessions, oldSessions) => {
      if (equals(newSessions, oldSessions)) {
        return;
      }

      this.processSessions();
    });
  };

  private checkForGoalsWithoutSession = (): IPromise<boolean> => {
    const sessionlessRequestParams: IRestLayerRequest = {
      fields: "id",
      limit: 1,
      filter: { sessionId: { $exists: false } },
    };

    return this.goalService.getGoalsV2(sessionlessRequestParams, null, { applyColoring: false }).then((response) => response.totalCount !== 0);
  };

  private watchForGoalsWithoutSession = () => {
    this.$scope.$watch<Session[]>("allSessions", (newSessions, oldSessions) => {
      if (equals(newSessions, oldSessions) || !oldSessions.length) {
        return;
      }

      this.checkForGoalsWithoutSession().then((res) => {
        this.$scope.hasGoalsWithoutSession = res;
      });
    });
  };

  private confirmDeletePlanningSession = (planningSession: IStratifiedPlanningSession) => {
    this.confirmDeleteSessionService.confirmDeleteSession(planningSession.data).subscribe({
      next: () => {
        this.$scope.$evalAsync();
      },
      error: (error) => this.uiErrorHandlingService.handleModal(error),
    });
  };

  private onToggleSessions = () => {
    this.$scope.showAllSessions = !this.$scope.showAllSessions;
    this.currentUserService.setUserSetting({ showArchivedSessions: this.$scope.showAllSessions });

    const trackEventMeta = { name: "show_archived_sessions", state: this.$scope.showAllSessions ? "enabled" : "disabled" };
    this.analyticsService.track(TrackingEvents.ToggleSet, trackEventMeta);

    this.processSessions();
  };

  private trackSessionsPageDataLoaded = (sessions: Session[]) => {
    this.analyticsService.track("Sessions Page Data Loaded", { sessions: sessions.length, is_archived_sessions_visible: this.$scope.showAllSessions });
  };

  private getNavItem = (session: Session): INavItem => {
    return this.favoritesManager.getFavoriteItem({ id: session.id }) || navItemsFactory.buildSessionNavItem(session);
  };

  private sessionIsFavorite = (session: Session): boolean => {
    return this.favoritesManager.entityIsFavorite({ id: session.id });
  };
}
